// Client-side backup system that works on Vercel
// Stores backups in localStorage and allows download

class ClientBackupManager {
  constructor() {
    this.storageKey = 'gitsawe_admin_backups';
    this.maxBackups = 10; // Keep only last 10 backups per file
  }

  // Create a backup before editing
  createBackup(fileName, data) {
    try {
      const backups = this.getAllBackups();
      const fileBackups = backups[fileName] || [];
      
      const backup = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        data: JSON.parse(JSON.stringify(data)), // Deep clone
        size: JSON.stringify(data).length
      };

      // Add new backup
      fileBackups.unshift(backup);
      
      // Keep only the most recent backups
      if (fileBackups.length > this.maxBackups) {
        fileBackups.splice(this.maxBackups);
      }

      backups[fileName] = fileBackups;
      localStorage.setItem(this.storageKey, JSON.stringify(backups));

      return {
        success: true,
        backupId: backup.id,
        message: `Backup created for ${fileName}`
      };
    } catch (error) {
      console.error('Backup creation failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get all backups
  getAllBackups() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load backups:', error);
      return {};
    }
  }

  // Get backups for a specific file
  getFileBackups(fileName) {
    const allBackups = this.getAllBackups();
    return allBackups[fileName] || [];
  }

  // Restore from backup
  restoreBackup(fileName, backupId) {
    try {
      const fileBackups = this.getFileBackups(fileName);
      const backup = fileBackups.find(b => b.id === backupId);
      
      if (!backup) {
        throw new Error('Backup not found');
      }

      return {
        success: true,
        data: backup.data,
        timestamp: backup.timestamp
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete a specific backup
  deleteBackup(fileName, backupId) {
    try {
      const backups = this.getAllBackups();
      const fileBackups = backups[fileName] || [];
      
      const filteredBackups = fileBackups.filter(b => b.id !== backupId);
      backups[fileName] = filteredBackups;
      
      localStorage.setItem(this.storageKey, JSON.stringify(backups));
      
      return {
        success: true,
        message: 'Backup deleted successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Download backup as file
  downloadBackup(fileName, backupId) {
    try {
      const result = this.restoreBackup(fileName, backupId);
      if (!result.success) {
        throw new Error(result.error);
      }

      const dataStr = JSON.stringify(result.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.backup.${new Date(result.timestamp).toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Download all backups as ZIP (requires JSZip library)
  async downloadAllBackups() {
    try {
      // This would require adding JSZip library
      // For now, we'll download as individual files
      const allBackups = this.getAllBackups();
      let downloadCount = 0;

      Object.entries(allBackups).forEach(([fileName, backups]) => {
        backups.forEach(backup => {
          setTimeout(() => {
            this.downloadBackup(fileName, backup.id);
          }, downloadCount * 100); // Stagger downloads
          downloadCount++;
        });
      });

      return {
        success: true,
        message: `Started download of ${downloadCount} backup files`
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Clear all backups
  clearAllBackups() {
    try {
      localStorage.removeItem(this.storageKey);
      return {
        success: true,
        message: 'All backups cleared'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get backup statistics
  getBackupStats() {
    try {
      const allBackups = this.getAllBackups();
      let totalBackups = 0;
      let totalSize = 0;
      const fileStats = {};

      Object.entries(allBackups).forEach(([fileName, backups]) => {
        const fileBackupCount = backups.length;
        const fileSize = backups.reduce((sum, backup) => sum + backup.size, 0);
        
        totalBackups += fileBackupCount;
        totalSize += fileSize;
        
        fileStats[fileName] = {
          count: fileBackupCount,
          size: fileSize,
          latest: backups[0]?.timestamp || null
        };
      });

      return {
        success: true,
        stats: {
          totalBackups,
          totalSize,
          totalFiles: Object.keys(allBackups).length,
          fileStats
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
export const clientBackup = new ClientBackupManager();

// Utility functions
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('am-ET', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};